import React from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { PageHeader } from '@/components/PageHeader';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { BookOpen, Bot, Loader2, Play, ShieldCheck, Square, Terminal, UserRoundCheck } from 'lucide-react';

const STEP_OPTIONS = [
  ['combined_full_identity', 'Combined: Google → hygiene → Reddit → capture'],
  ['google_mobile_signup', 'Google mobile signup'],
  ['google_hygiene_totp', 'Remove phone + add TOTP'],
  ['reddit_mobile_signup', 'Reddit mobile signup'],
  ['reddit_sign_in_capture', 'Reddit sign-in + capture'],
  ['human_takeover', 'Human takeover checkpoint'],
];

export default function AutomationDocs() {
  const qc = useQueryClient();
  const [agentId, setAgentId] = React.useState('');
  const [executionMode, setExecutionMode] = React.useState('autonomous');
  const [runMode, setRunMode] = React.useState('once');
  const [batchCount, setBatchCount] = React.useState(1);
  const [countryIso, setCountryIso] = React.useState('CO');
  const [redditStrategy, setRedditStrategy] = React.useState('email');
  const [steps, setSteps] = React.useState(['combined_full_identity']);

  const agentsQ = useQuery({ queryKey: ['agents', 'automation-docs'], queryFn: () => api.get('/agents').then(r => r.data) });
  const playbooksQ = useQuery({ queryKey: ['automation-playbooks'], queryFn: () => api.get('/automation/playbooks').then(r => r.data) });
  const toolsQ = useQuery({ queryKey: ['master-tools'], queryFn: () => api.get('/master/tools').then(r => r.data) });

  const mobileAgents = (agentsQ.data?.items || []).filter((a) => a.runtime === 'mobile' && a.vmos_pad_code && a.status !== 'archived');

  React.useEffect(() => {
    if (!agentId && mobileAgents[0]?.id) setAgentId(mobileAgents[0].id);
  }, [agentId, mobileAgents]);

  const runMut = useMutation({
    mutationFn: () => api.post('/automation/mobile-identity/run', {
      agent_id: agentId,
      steps,
      execution_mode: executionMode,
      run_mode: runMode,
      batch_count: Number(batchCount) || 1,
      country_iso: countryIso,
      reddit_strategy: redditStrategy,
    }).then(r => r.data),
    onSuccess: (res) => {
      toast.success('Automation queued', { description: `${res.queued?.length || 0} task/checkpoint entries created` });
      qc.invalidateQueries({ queryKey: ['tasks'] });
    },
    onError: (e) => toast.error(e?.response?.data?.detail || 'Could not queue automation'),
  });

  const stopMut = useMutation({
    mutationFn: () => api.post('/automation/mobile-identity/stop', { agent_id: agentId || null, cancel_in_flight: true }).then(r => r.data),
    onSuccess: (res) => toast.success('Automation stopped', { description: `${res.cancelled_tasks || 0} tasks cancelled` }),
    onError: (e) => toast.error(e?.response?.data?.detail || 'Could not stop automation'),
  });

  const toggleStep = (key) => {
    if (key === 'combined_full_identity') return setSteps(['combined_full_identity']);
    const withoutCombined = steps.filter((s) => s !== 'combined_full_identity');
    setSteps(withoutCombined.includes(key) ? withoutCombined.filter((s) => s !== key) : [...withoutCombined, key]);
  };

  return (
    <div className="space-y-6" data-testid="automation-docs-page">
      <PageHeader
        eyebrow="Docs"
        title="Automation Playbooks"
        description="Run VMOS identity flows from one place, or inspect every skill the Master Agent can call."
      />

      <Tabs defaultValue="run" className="space-y-4">
        <TabsList data-testid="automation-docs-tabs">
          <TabsTrigger value="run" data-testid="automation-run-tab">Run playbook</TabsTrigger>
          <TabsTrigger value="steps" data-testid="automation-steps-tab">Steps</TabsTrigger>
          <TabsTrigger value="tools" data-testid="automation-tools-tab">Master tools</TabsTrigger>
        </TabsList>

        <TabsContent value="run" className="grid gap-4 xl:grid-cols-[1.1fr_0.9fr]">
          <section className="space-y-4 rounded-lg border border-white/10 bg-[hsl(var(--surface-1))] p-4" data-testid="mobile-identity-run-card">
            <div className="flex items-center gap-2">
              <Bot className="h-5 w-5 text-[hsl(var(--primary))]" />
              <h2 className="text-lg font-semibold">Mobile Identity Factory</h2>
              <Badge variant="outline">VMOS</Badge>
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-1.5">
                <Label>Mobile agent</Label>
                <Select value={agentId} onValueChange={setAgentId}>
                  <SelectTrigger data-testid="automation-agent-select"><SelectValue placeholder="Select a VMOS agent" /></SelectTrigger>
                  <SelectContent>
                    {mobileAgents.map((a) => (
                      <SelectItem key={a.id} value={a.id} data-testid={`automation-agent-option-${a.id}`}>{a.name} · {a.vmos_pad_code}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Mode</Label>
                <Select value={executionMode} onValueChange={setExecutionMode}>
                  <SelectTrigger data-testid="automation-execution-mode-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="autonomous">Full autonomous</SelectItem>
                    <SelectItem value="human_loop">Human-in-the-loop</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Run type</Label>
                <Select value={runMode} onValueChange={setRunMode}>
                  <SelectTrigger data-testid="automation-run-mode-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="once">Run once</SelectItem>
                    <SelectItem value="batch">Run N accounts</SelectItem>
                    <SelectItem value="continuous">Continuous until stopped</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Batch count</Label>
                <Input type="number" min="1" max="100" value={batchCount} onChange={(e) => setBatchCount(e.target.value)} data-testid="automation-batch-count-input" />
              </div>
              <div className="space-y-1.5">
                <Label>SMS country</Label>
                <Input value={countryIso} onChange={(e) => setCountryIso(e.target.value.toUpperCase().slice(0, 2))} data-testid="automation-country-input" />
              </div>
              <div className="space-y-1.5">
                <Label>Reddit strategy</Label>
                <Select value={redditStrategy} onValueChange={setRedditStrategy}>
                  <SelectTrigger data-testid="automation-reddit-strategy-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="email">Email + Gmail OTP</SelectItem>
                    <SelectItem value="google">Google OAuth</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Steps</Label>
              <div className="grid gap-2 md:grid-cols-2">
                {STEP_OPTIONS.map(([key, label]) => (
                  <label key={key} className="flex items-center gap-2 rounded-md border border-white/10 bg-[hsl(var(--surface-2))] px-3 py-2 text-sm" data-testid={`automation-step-${key}`}>
                    <Checkbox checked={steps.includes(key)} onCheckedChange={() => toggleStep(key)} data-testid={`automation-step-checkbox-${key}`} />
                    <span>{label}</span>
                  </label>
                ))}
              </div>
            </div>

            <div className="flex flex-wrap gap-2 pt-2">
              <Button onClick={() => runMut.mutate()} disabled={!agentId || runMut.isPending} data-testid="automation-run-button">
                {runMut.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Play className="mr-2 h-4 w-4" />}
                Start automation
              </Button>
              <Button variant="destructive" onClick={() => stopMut.mutate()} disabled={!agentId || stopMut.isPending} data-testid="automation-stop-button">
                {stopMut.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Square className="mr-2 h-4 w-4" />}
                Stop automation
              </Button>
            </div>
          </section>

          <section className="rounded-lg border border-white/10 bg-[hsl(var(--surface-1))] p-4" data-testid="automation-mode-guide">
            <div className="mb-3 flex items-center gap-2">
              <ShieldCheck className="h-5 w-5 text-emerald-400" />
              <h2 className="text-lg font-semibold">Operating rules</h2>
            </div>
            <ul className="space-y-3 text-sm text-[hsl(var(--text-2))]">
              <li><b>Autonomous</b> runs without pausing unless the worker hits a hard blocker.</li>
              <li><b>Human-in-the-loop</b> carries takeover markers and can launch manual sessions for stuck screens.</li>
              <li><b>Batch</b> queues a bounded number of accounts. Use this before any high-volume run.</li>
              <li><b>Continuous</b> keeps re-queueing the selected batch after the previous factory tasks finish; Stop cancels active signup tasks.</li>
              <li>Recommended Reddit path is <b>Email + Gmail OTP</b>; Google OAuth is currently more block-prone.</li>
            </ul>
          </section>
        </TabsContent>

        <TabsContent value="steps">
          <div className="grid gap-3 lg:grid-cols-2" data-testid="automation-steps-list">
            {(playbooksQ.data?.mobile_identity_factory?.steps || []).map((step) => (
              <article key={step.key} className="rounded-lg border border-white/10 bg-[hsl(var(--surface-1))] p-4" data-testid={`automation-step-doc-${step.key}`}>
                <div className="mb-2 flex items-center justify-between gap-2">
                  <h3 className="font-semibold">{step.label}</h3>
                  <Badge variant="outline">{step.task_kind}</Badge>
                </div>
                <ul className="space-y-1 text-sm text-[hsl(var(--text-2))]">
                  {step.does.map((line) => <li key={line}>• {line}</li>)}
                </ul>
              </article>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="tools">
          <section className="rounded-lg border border-white/10 bg-[hsl(var(--surface-1))]" data-testid="master-tools-list">
            <div className="flex items-center gap-2 border-b border-white/10 px-4 py-3">
              <Terminal className="h-5 w-5 text-[hsl(var(--primary))]" />
              <h2 className="text-lg font-semibold">Master Agent skills and tools</h2>
              <Badge variant="outline">{toolsQ.data?.total || 0}</Badge>
            </div>
            <ScrollArea className="h-[560px]">
              <div className="divide-y divide-white/10">
                {(toolsQ.data?.items || []).map((item) => {
                  const fn = item.function || {};
                  return (
                    <article key={fn.name} className="px-4 py-3" data-testid={`master-tool-${fn.name}`}>
                      <div className="mb-1 flex flex-wrap items-center gap-2">
                        <BookOpen className="h-4 w-4 text-[hsl(var(--text-3))]" />
                        <h3 className="font-mono text-sm font-semibold">{fn.name}</h3>
                      </div>
                      <p className="text-sm text-[hsl(var(--text-2))]">{fn.description}</p>
                    </article>
                  );
                })}
              </div>
            </ScrollArea>
          </section>
        </TabsContent>
      </Tabs>
    </div>
  );
}
